import PageContainer from "../PageContainer";
import { UnitForm } from "./UnitForm";
import { useMemo, useState } from "react";
import { usePreload } from "../../../contexts/DataContext/PreloadContext";
import { editUnit, fetchUnits } from "../../../services/unitService";
import { Link, Navigate, useParams } from "react-router-dom";
import { unitsPageCache } from "../../../pages/cache/PageCache";
import { usePagedQuery } from "../../../contexts/hooks/usePagedQuery";
import {
  Stack,
  Tooltip,
  IconButton,
  Alert,
  Button,
  CircularProgress,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { localizeField } from "../../../config/localization";

const EditUnit = () => {
  const { showNotification } = usePreload();
  const [updating, setUpdating] = useState(false);
  const { id: unitCode } = useParams();

  const {
    data: rows,
    reload,
    fetched,
    isLoading,
    error,
  } = usePagedQuery({
    fetchFunction: fetchUnits,
    searchOption: [{ id: "unitCode", display: "Code" }],
    cache: unitsPageCache,
    language: "all",
    searchKey: unitCode,
    captureErrors: true,
  });

  const row = useMemo(() => {
    return {
      ...rows[0],
      unitName: localizeField(rows, "unitName"),
      unitAbbreviation: localizeField(rows, "unitAbbreviation"),
      unitDisplayName: localizeField(rows, "unitDisplayName"),
      unitDescription: localizeField(rows, "unitDescription"),
      unitTooltip: localizeField(rows, "unitTooltip"),
    };
  }, [rows]);

  if (!unitCode || (fetched && !rows[0]?.unitCode)) {
    return <Navigate to="/notfound" replace />;
  }

  const onUpdate = async (updatedRow) => {
    try {
      setUpdating(true);
      const response = await editUnit(updatedRow);
      if (response instanceof Response && !response.ok)
        throw new Error("Save failed");
      unitsPageCache.clear();

      showNotification(`${updatedRow?.unitCode} was updated!`, "success");
      return true;
    } catch (e) {
      console.log(e);
      showNotification(
        `something went wrong when updating the ${updatedRow?.unitCode} unit.`,
        "error",
      );
      return false;
    } finally {
      setUpdating(false);
    }
  };
  return (
    <PageContainer
      title="Edit Unit"
      subtitle="Update the symbol and localized details for this ETIM unit"
      breadcrumbs={[
        { title: "ETIM Units", path: "/unitsnew" },
        { title: "Edit" },
      ]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Back" placement="bottom" enterDelay={300}>
            <span>
              <IconButton
                size="small"
                aria-label="Back to units"
                component={Link}
                to="/unitsnew"
              >
                <KeyboardBackspaceIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      }
    >
      {error ? (
        <Alert
          severity="error"
          action={<Button onClick={() => reload(true)}>Retry</Button>}
        >
          Unable to load unit.
        </Alert>
      ) : isLoading || !fetched ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress aria-label="Loading unit" />
        </Stack>
      ) : (
        <UnitForm
          onSave={onUpdate}
          onRefresh={() => reload(true)}
          props={{
            row,
            uploading: updating,
            saveText: "Save Changes",
            isUpdate: true,
          }}
        />
      )}
    </PageContainer>
  );
};

export default EditUnit;
